import {
  Question,
  type Questionnaire,
  type QuestionnairePage,
} from '@/lib/questionnaires/Questionnaire'
import clsx from 'clsx'
import PersonalInformationReviewList from './PersonalInformationReviewList'
import { PatientProfile } from '@/lib/apis/types'
import NcMarkdown from '@/shared/components/NcMarkdown'
import SupportingDocumentItem, {
  SupportingDocument,
} from '@/features/prescriptions/SupportingDocumentItem'
import React from 'react'

type Padding = {
  x: number
  y: number
  smx: number
}

type QuestionnaireReviewProps<T extends { [index: string]: any }> = {
  questionnaire: Questionnaire
  values: T

  // Customization made for certificate view in the portal for the patients, probably not the best way...
  padding?: Padding
  documents: Array<SupportingDocument>
}

export default function QuestionnaireReview<
  T extends { [index: string]: any },
>({ questionnaire, values, documents }: QuestionnaireReviewProps<T>) {
  return (
    <>
      <dl className="grid grid-cols-6 gap-y-4">
        {questionnaire.pages.map((page, idx) => (
          <QuestionnaireAnswerList
            key={`page-${idx}`}
            page={page}
            values={values}
          />
        ))}
      </dl>
      {documents.length > 0 && (
        <div className="mt-6 flex flex-col gap-y-2">
          <h2 className="text-lg font-semibold tracking-tight text-gray-800">
            Supporting Documents
          </h2>
          <ul className="flex flex-col gap-y-3">
            {documents.map((document) => (
              <SupportingDocumentItem
                key={document.name}
                document={document}
                editable={false}
                enableDownload={false}
              />
            ))}
          </ul>
        </div>
      )}
    </>
  )
}

type QuestionnaireAnswerListProps<T extends { [index: string]: any }> = {
  page: QuestionnairePage
  values: T
}

function QuestionnaireAnswerList<T extends { [index: string]: any }>({
  page,
  values,
}: QuestionnaireAnswerListProps<T>) {
  if (page === 'personal-information') {
    return (
      <PersonalInformationReviewList
        values={values as unknown as PatientProfile}
      />
    )
  }

  if (page === 'upload-supporting-document' || page === 'call-arrangement')
    return null

  return (
    <>
      {page.questions.map((question, idx) => (
        <QuestionnaireAnswer key={idx} question={question} values={values} />
      ))}
    </>
  )
}

type QuestionnaireAnswerProps<T extends { [index: string]: any }> = {
  question: Question
  values: T
}

function QuestionnaireAnswer<T extends { [index: string]: any }>({
  question,
  values,
}: QuestionnaireAnswerProps<T>) {
  switch (question.type) {
    case 'date-range':
      if (question.startKey in values && question.endKey in values) {
        return (
          <div className="col-span-full grid grid-cols-6 gap-y-2">
            <div className="col-span-6 flex flex-col sm:col-span-3">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                <NcMarkdown>{question.startTitle}</NcMarkdown>
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700">
                {values[question.startKey]}
              </dd>
            </div>
            <div className="col-span-6 flex flex-col sm:col-span-3">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                <NcMarkdown>{question.endTitle}</NcMarkdown>
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700">
                {values[question.endKey]}
              </dd>
            </div>
          </div>
        )
      } else {
        return null
      }
    case 'date':
    case 'free-text':
      if (question.key in values && !!values[question.key]) {
        return (
          <div className={clsx('col-span-full grid grid-cols-6')}>
            <div className="col-span-full flex flex-col">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                <NcMarkdown>{question.title}</NcMarkdown>
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700">
                {values[question.key]}
              </dd>
            </div>
          </div>
        )
      } else {
        return null
      }

    case 'multiple-choice':
      if (!(question.key in values) || !values[question.key]) return null

      const answerKeys = values[question.key]

      if (!Array.isArray(answerKeys)) return null

      const answers = question.options.filter((option) =>
        answerKeys.includes(option.id),
      )

      if (answers.length === 0) return null

      return (
        <div className={clsx('col-span-full grid grid-cols-6')}>
          <div className="col-span-full flex flex-col">
            <dt className="text-sm font-medium leading-6 text-gray-900">
              <NcMarkdown>{question.title}</NcMarkdown>
            </dt>
            <dd className="mt-1 text-sm leading-6 text-gray-700">
              {answers.map((ans) => (
                <li
                  key={`${question.key}-${ans.id}`}
                  className="flex flex-col gap-y-1.5"
                >
                  - {ans.label}
                </li>
              ))}
            </dd>
          </div>
        </div>
      )

    case 'single-choice':
      if (!(question.key in values) || !values[question.key]) return null

      const answer = question.options.find(
        (option) => option.id === values[question.key],
      )

      if (!answer) return null

      return (
        <div className={clsx('col-span-full grid grid-cols-6')}>
          <div className="col-span-full flex flex-col">
            <dt className="text-sm font-medium leading-6 text-gray-900">
              <NcMarkdown>{question.title}</NcMarkdown>
            </dt>
            <dd className="mt-1 text-sm leading-6 text-gray-700">
              {answer.label}
            </dd>
          </div>
        </div>
      )

    case 'yes-no':
      if (!(question.key in values) || !values[question.key]) return null

      return (
        <div className={clsx('col-span-full grid grid-cols-6')}>
          <div className="col-span-full flex flex-col">
            <dt className="text-sm font-medium leading-6 text-gray-900">
              <NcMarkdown>{question.title}</NcMarkdown>
            </dt>
            <dd className="mt-1 text-sm leading-6 text-gray-700">
              {values[question.key]}
            </dd>
          </div>
        </div>
      )

    case 'duration':
      const labels: Array<string> = []
      if (values[question.yearKey] && values[question.yearKey] > 0) {
        if (values[question.yearKey] > 1)
          labels.push(`${values[question.yearKey]} years`)
        else labels.push(`${values[question.yearKey]} year`)
      }
      if (values[question.monthKey] && values[question.monthKey] > 0) {
        if (values[question.monthKey] > 1)
          labels.push(`${values[question.monthKey]} months`)
        else labels.push(`${values[question.monthKey]} month`)
      }

      if (labels.length > 0)
        return (
          <div className={clsx('col-span-full grid grid-cols-6')}>
            <div className="col-span-full flex flex-col">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                <NcMarkdown>{question.title}</NcMarkdown>
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700">
                {labels.join(', ')}
              </dd>
            </div>
          </div>
        )
      else return null

    default:
      return null
  }
}
